import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import EstimateList from "./EstimateList";
import DsShell from "./ds/DsShell";
import PasswordGate from "./PasswordGate";
import { AUTH_ENABLED, AuthGate } from "./auth/AuthGate";
import { currentEmail, signOut } from "./auth/session";
import RequireRole from "./auth/RequireRole";
import { useAuth } from "./auth/AuthContext";
import { roleAtLeast, minRoleFor, canAccess } from "./auth/access";
import StaffAdmin from "./StaffAdmin";
import ConnectionBanner from "./components/ConnectionBanner";
// tart(FloatingAssistant) 숨김 — QCC로 흡수 예정, 컴포넌트/로직/테스트는 보존.
// import FloatingAssistant from "./FloatingAssistant";
import ErrorBoundary from "./ErrorBoundary";

// Heavy 라우트 — route-level code splitting (vite 자동 chunk)
const CreatePage = lazy(() => import("./ds/CreatePage"));
const DashboardPage = lazy(() => import("./dash/DashboardPage"));
const RecipeDBPage = lazy(() => import("./RecipeDBPage"));
const QuickEstimateChat = lazy(() => import("./QuickEstimateChat"));
const QuickEstimate = lazy(() => import("./QuickEstimate"));
const SiteEstimate = lazy(() => import("./SiteEstimate"));
const ManualEstimate = lazy(() => import("./ManualEstimate"));
const QuickEstimateEditor = lazy(() => import("./QuickEstimateEditor"));
const QuickEstimatePDFView = lazy(() => import("./quick-v2/QuickEstimatePDFView"));
const BalanceSheet = lazy(() => import("./BalanceSheet"));
const SimpleEstimate = lazy(() => import("./SimpleEstimate"));
const SimpleEstimateV2 = lazy(() => import("./SimpleEstimateV2"));
const TrashPage = lazy(() => import("./TrashPage"));
const BrandsPage = lazy(() => import("./BrandsPage"));
const FranchiseHubPage = lazy(() => import("./FranchiseHubPage"));
const FranchiseEstimate = lazy(() => import("./FranchiseEstimate"));

// 셸(범위3) 신규 화면 — B-3a. 기존 화면 이주는 B-3b.
const SettingsPage = lazy(() => import("./ds/SettingsPage"));
const UnmatchedPage = lazy(() => import("./ds/UnmatchedPage"));
const DefaultsPage = lazy(() => import("./ds/DefaultsPage"));
const SolapiSlackSettingsPage = lazy(() => import("./ds/SolapiSlackSettingsPage"));
const QccPage = lazy(() => import("./ds/QccPage"));
const MorePage = lazy(() => import("./ds/MorePage"));
const Settlement = lazy(() => import("./Settlement"));
const ShareView = lazy(() => import("./ShareView"));   // G10 고객 공유 열람(공개)
const SignView = lazy(() => import("./sign/SignView"));         // 전자계약 고객 서명(공개)
const SignCheckPage = lazy(() => import("./sign/SignCheckPage")); // 손서명 점검(공개, 계약 없이 — 2026-09-29)
const VerifyDocPage = lazy(() => import("./sign/VerifyDocPage")); // 전자서명 문서 검증(공개)
const SignedView = lazy(() => import("./sign/SignedView"));   // 참조자 완료본 열람(공개)
const BalanceView = lazy(() => import("./sign/BalanceView")); // 잔금내역서 고객 열람(공개)
const ClipboardPage = lazy(() => import("./ds/ClipboardPage"));   // 행 클립보드(장바구니)
const DocsPage = lazy(() => import("./docs/DocsPage"));           // 공사서류(문서 탭) — 구 WorkDocs 흡수
const DocEditor = lazy(() => import("./docs/DocEditor"));
const SettlementDetail = lazy(() => import("./SettlementDetail"));
const SettlementCorrections = lazy(() => import("./SettlementCorrections"));
const ContractHome = lazy(() => import("./contract/ContractHome"));     // 계약서 — 구 ig-contract 흡수
const ContractEditor = lazy(() => import("./contract/ContractEditor"));
const EsignPage = lazy(() => import("./esign/EsignPage"));             // 전자계약 관리(발송·서명·증거)
const EsignSealsPage = lazy(() => import("./esign/EsignSealsPage"));   // 설정 › 전자계약 서명(업체별 서명이름·도장)
const JoinPage = lazy(() => import("./auth/JoinPage"));                // 초대 링크 가입(공개)
const SmbmFrame = lazy(() => import("./smbm/SmbmFrame"));              // SMBM(머니앱) 앱 내 임베드
const PortalApp = lazy(() => import("./portal/PortalApp"));            // 고객 포털(my.igggstudio.com 전용 앱)
const PortalHome = lazy(() => import("./portal/PortalHome"));          // 포털 본체(공개 라우트 /p/:ptoken)
const AsAdminPage = lazy(() => import("./as/AsAdminPage"));            // AS·하자보수 내부 관리(manager+)
const PipelinePage = lazy(() => import("./pipeline/PipelinePage"));    // 영업 파이프라인(owner)

// 라우트별 개별 격리 — 한 페이지 에러가 다른 페이지까지 죽이지 않음.
const RouteFallback = (
  <div
    style={{
      minHeight: "100dvh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#e8ebe6",
      color: "#454745",
      fontSize: 14,
      fontFamily: "Inter, system-ui, sans-serif",
    }}
  >
    페이지 로딩 중…
  </div>
);
const EB = ({ children }) => (
  <ErrorBoundary>
    <Suspense fallback={RouteFallback}>{children}</Suspense>
  </ErrorBoundary>
);

export default function App() {
  // 고객 포털 호스트(my.igggstudio.com) — 직원 셸·게이트를 전혀 거치지 않는 별도 앱 표면.
  // 같은 Vercel 프로젝트에 도메인 alias 만 추가(2026-07-25). VITE_PORTAL_HOST=1 은 dev 관측용.
  const isPortalHost =
    typeof window !== "undefined" &&
    (window.location.hostname === "my.igggstudio.com" ||
     import.meta.env?.VITE_PORTAL_HOST === "1");
  if (isPortalHost) {
    return <EB><PortalApp /></EB>;
  }
  // AS 전용 호스트(as.igggstudio.com) — 현장 직원이 바로 여는 단독 화면(사장 지시
  // 2026-07-27). 직원 로그인은 그대로 거친다(고객 포털과 달리 내부 데이터).
  const isAsHost =
    typeof window !== "undefined" &&
    (window.location.hostname === "as.igggstudio.com" ||
     import.meta.env?.VITE_AS_HOST === "1");
  if (isAsHost) {
    return <AsApp />;
  }
  // 고객 문서 호스트(sign.igggstudio.com) — 서명·공유 링크(공개 경로)만 서빙.
  // 루트·오타 URL 이 직원 로그인 카드로 떨어지던 결함(전수감사 2026-07-28)
  // → 공개 경로가 아니면 고객 안내 랜딩(my. 와 동일 문법).
  const isSignHost =
    typeof window !== "undefined" &&
    (window.location.hostname === "sign.igggstudio.com" ||
     import.meta.env?.VITE_SIGN_HOST === "1");
  if (isSignHost && !isPublicPathname()) {
    return <SignLanding />;
  }
  return <StaffApp />;
}

/** 공개(비로그인) 경로 판정 단일 SoT — StaffApp 게이트 우회와 sign. 호스트 분기가 공유. */
function isPublicPathname() {
  if (typeof window === "undefined") return false;
  const p = window.location.pathname;
  return p.startsWith("/share/") || p.startsWith("/sign/") || p.startsWith("/signed/")
    || p.startsWith("/balance-view/") || p.startsWith("/p/") || p.startsWith("/join/")
    || p === "/verify";
}

/** sign. 루트 고객 안내 — 직원 브랜드·로그인 노출 금지(PortalApp Landing 과 동일 톤). */
function SignLanding() {
  useEffect(() => { document.title = "이견공간 — 전자계약·문서"; }, []);
  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "#eef0f3", color: "#1b1e23", padding: 24,
      fontFamily: "'Pretendard Variable', Pretendard, 'Apple SD Gothic Neo', sans-serif",
    }}>
      <div style={{ textAlign: "center", lineHeight: 1.9 }}>
        <div className="iggg-wordmark" style={{ fontSize: 14 }}>iggg studio</div>
        <div style={{ fontSize: 15, marginTop: 8 }}>
          전자계약·문서 열람 전용 페이지입니다.<br />문자·카카오톡으로 받은 링크로 접속해 주세요.
        </div>
      </div>
    </div>
  );
}

function AsApp() {
  return (
    <EB>
      <BrowserRouter>
        <AuthGate>
          <AsStandaloneShell />
        </AuthGate>
      </BrowserRouter>
    </EB>
  );
}

function AsStandaloneShell() {
  // 호스트별 탭 타이틀(전수감사 P1 — as. 탭이 'ig works'로만 보이던 것)
  useEffect(() => { document.title = "AS유지보수 — ig works"; }, []);
  return (
    <div style={{ minHeight: "100dvh", background: "var(--ds-canvas, #fff)" }}>
      <header style={{ display: "flex", alignItems: "center", gap: 10,
                       padding: "10px 16px", borderBottom: "1px solid var(--ds-line, #e6e9ee)" }}>
        <span style={{ fontWeight: 800, fontSize: 15, color: "var(--ds-ink, #1b1e23)" }}>
          ig works · AS유지보수
        </span>
        <span style={{ flex: 1 }} />
        <AsShellAccount />
      </header>
      <RequireRole feature="as_ops">
        <Suspense fallback={RouteFallback}><AsAdminPage /></Suspense>
      </RequireRole>
    </div>
  );
}

/** sso-gate.js 가 보낸 복귀 — 로그인 완료 후 원래 앱으로 돌려보낸다(사장 확정
 *  2026-07-27 SSO). 우리 도메인만 허용(오픈 리다이렉트 방지). AuthGate 통과 뒤에만
 *  렌더되므로 미로그인 사용자는 로그인 화면을 먼저 거친다. */
function SsoReturnRedirect() {
  useEffect(() => {
    const r = new URLSearchParams(window.location.search).get("sso_return");
    if (!r) return;
    try {
      const u = new URL(r);
      if (u.protocol === "https:"
          && (u.hostname === "igggstudio.com" || u.hostname.endsWith(".igggstudio.com"))) {
        window.location.replace(r);
      }
    } catch { /* 잘못된 값 — 무시하고 홈 */ }
  }, []);
  return null;
}

/** 홈 분기 — 현장팀(estimate 미달)은 견적 홈 대신 AS 보드로(사장 확정 2026-07-27). */
function HomeGate() {
  const { role, access, deny } = useAuth();
  if (!canAccess(role, "estimate", access, deny)) {
    return <Navigate to="/as-admin" replace />;
  }
  // 첫 화면 = 현황 대시보드(사장 2026-08-03). 견적 만들기는 /create(메뉴 유지).
  return <EB><DashboardPage /></EB>;
}

function AsShellAccount() {
  const email = currentEmail();
  if (!AUTH_ENABLED) return null;
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 8,
                   fontSize: 12, color: "var(--ds-mute, #8a8f98)" }}>
      {email}
      <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm" onClick={() => signOut()}>
        로그아웃
      </button>
    </span>
  );
}

function StaffApp() {
  const [authed, setAuthed] = useState(
    () => localStorage.getItem("iggg_auth") === "true"
  );
  // G1(2026-07-05): VITE_AUTH=firebase 면 개인 계정 로그인이 공용 비밀번호 게이트를 대체.
  // G10: 고객 공유 열람(/share/:token)·전자서명(/sign/:token)·문서 검증(/verify)·
  // 초대 가입(/join)은 공개 — 로그인 게이트 우회. 고객·초대자가 로그인에 걸리면 안 된다.
  const isPublicShare = isPublicPathname();   // 판정 단일 SoT — sign. 호스트 분기와 공유

  // 공용 PIN 모드(AUTH 미설정)만 여기서 게이트. firebase 모드는 아래 AuthGate wrapper 가
  // 로그인·가입·승인대기까지 처리한다(2026-07-24 완전 전환).
  if (!isPublicShare && !AUTH_ENABLED && !authed) {
    return <PasswordGate onSuccess={() => setAuthed(true)} />;
  }

  const appTree = (
    <ErrorBoundary>
      <BrowserRouter>
        {/* 전역 연결 상태 배너 — 직원 화면 전용(2026-06-11). 고객 공개 페이지(/sign·/verify·
            /share)에는 내부 인프라 경고를 노출하지 않는다 — 고객이 당황하고 신뢰를 해침.
            공개 페이지는 각자 자체 오류 안내(만료·네트워크 문구)로 처리(2026-07-19). */}
        {!isPublicShare && <ConnectionBanner />}
        {!isPublicShare && <SsoReturnRedirect />}
        <Routes>
          {/* ── 고객 공유 열람 (공개, 게이트 우회) ── */}
          <Route path="/share/:token" element={<EB><ShareView /></EB>} />
          {/* ── 전자계약 서명·문서 검증 (공개, 게이트 우회) ── */}
          <Route path="/sign/check" element={<EB><SignCheckPage /></EB>} />
          <Route path="/sign/:token" element={<EB><SignView /></EB>} />
          <Route path="/verify" element={<EB><VerifyDocPage /></EB>} />
          <Route path="/signed/:vtoken" element={<EB><SignedView /></EB>} />
          <Route path="/balance-view/:btoken" element={<EB><BalanceView /></EB>} />
          <Route path="/p/:ptoken" element={<EB><PortalHome /></EB>} />
          {/* ── 초대 링크 가입 (공개, 게이트 우회) ── */}
          <Route path="/join/:token" element={<EB><JoinPage /></EB>} />
          {/* ── 기존 목록/분석 직접 접근 보존 (구 진입 경로) ── */}
          <Route path="/legacy/list" element={<EB><EstimateList /></EB>} />

          {/* ── 활성 라우트 (estimateMenuConfig enabled:true) ── */}
          <Route path="/estimate/simple" element={<EB><SimpleEstimate /></EB>} />
          <Route path="/estimate/quick" element={<EB><QuickEstimate /></EB>} />
          <Route path="/estimate/site/new" element={<EB><SiteEstimate /></EB>} />
          <Route path="/estimate/manual" element={<EB><ManualEstimate /></EB>} />
          <Route path="/estimate/quick/:id/pdf" element={<EB><QuickEstimatePDFView /></EB>} />
          <Route path="/estimate/quick/:id" element={<EB><QuickEstimateEditor /></EB>} />
          <Route path="/estimate/franchise/new" element={<EB><FranchiseEstimate mode="new" /></EB>} />
          <Route path="/estimate/franchise/:id" element={<EB><FranchiseEstimate mode="edit" /></EB>} />
          <Route path="/balance/new" element={<EB><RequireRole feature="balance"><BalanceSheet /></RequireRole></EB>} />
          <Route path="/balance/:id" element={<EB><RequireRole feature="balance"><BalanceSheet /></RequireRole></EB>} />

          {/*
            ── LEGACY 라우트 (estimateMenuConfig enabled:false — 신규 진입점 X) ──
            기존 저장된 견적이 이 URL 로 직접 접근/재오픈하는 흐름이 살아있어 라우트
            는 보존. 컴포넌트 / 라우트 둘 다 함부로 삭제하지 말 것 (저장된 데이터의
            re-render 경로). 신규 기능 추가는 활성 라우트에서만.
          */}
          <Route path="/estimate/simple-v2" element={<EB><SimpleEstimateV2 /></EB>} />
          <Route path="/estimate/quick-legacy" element={<EB><QuickEstimateChat /></EB>} />

          {/*
            ── 셸 그룹 (범위3) ──
            DsShell(사이드바+하단탭) 레이아웃 + Outlet.
            B-3a: 신규 화면(/settings·/qcc·/more).
            B-3b: 툴 3화면(단가DB·브랜드·휴지통) 이주.
            B-3c: '/'=대시보드(EstimateList) 단일 + '/create'=견적만들기(CreatePage) + breakpoint 768.
          */}
          <Route element={<EB><DsShell /></EB>}>
            <Route path="/" element={<HomeGate />} />
            <Route path="/dashboard" element={<EB><RequireRole feature="estimate"><EstimateList /></RequireRole></EB>} />
            <Route path="/create" element={<EB><RequireRole feature="estimate"><CreatePage /></RequireRole></EB>} />
            {/* 설정 자체는 전원(테마). 내부 항목은 역할별: 미등록단가=관리자+, 사업자정보·
                전자계약서명·메시지발송알림=사장 전용. SettingsPage 도 같은 게이트. */}
            <Route path="/settings" element={<EB><SettingsPage /></EB>} />
            <Route path="/settings/unmatched" element={<EB><RequireRole feature="unmatched"><UnmatchedPage /></RequireRole></EB>} />
            <Route path="/settings/defaults" element={<EB><RequireRole feature="bizinfo"><DefaultsPage /></RequireRole></EB>} />
            <Route path="/settings/esign-seals" element={<EB><RequireRole feature="esignseals"><EsignSealsPage /></RequireRole></EB>} />
            <Route path="/settings/message-alerts" element={<EB><RequireRole min="owner"><SolapiSlackSettingsPage /></RequireRole></EB>} />
            {/* QCC — 현장팀 개방(사장 지시 2026-07-28). 견적 계열과 별도 키. */}
            <Route path="/qcc" element={<EB><RequireRole feature="qcc"><QccPage /></RequireRole></EB>} />
            {/* 정산 그룹 — 사장(owner) 전용. PIN 폐지 → 역할 가드로 대체. */}
            <Route path="/settlement" element={<EB><RequireRole feature="settlement"><Settlement /></RequireRole></EB>} />
            <Route path="/settlement/corrections" element={<EB><RequireRole feature="settlement"><SettlementCorrections /></RequireRole></EB>} />
            <Route path="/settlement/:siteId" element={<EB><RequireRole feature="settlement"><SettlementDetail /></RequireRole></EB>} />
            {/* 계약서·전자계약 — 관리자 이상(사용자 미세조정 2026-07-24, 직원 제외). */}
            <Route path="/contract" element={<EB><RequireRole feature="contract"><ContractHome /></RequireRole></EB>} />
            <Route path="/contract/new" element={<EB><RequireRole feature="contract"><ContractEditor /></RequireRole></EB>} />
            <Route path="/contract/:key" element={<EB><RequireRole feature="contract"><ContractEditor /></RequireRole></EB>} />
            <Route path="/esign" element={<EB><RequireRole feature="esign"><EsignPage /></RequireRole></EB>} />
            <Route path="/more" element={<EB><MorePage /></EB>} />
            {/* 단가 DB·브랜드 — 접근 config(기본 관리자+). 단가는 견적 가격 근원. */}
            <Route path="/recipe-db" element={<EB><RequireRole feature="recipedb"><RecipeDBPage /></RequireRole></EB>} />
            {/* 프랜차이즈·클립보드·휴지통 = 견적 계열(access 'estimate') — 가드 누락으로
                현장팀이 URL 직접 입력으로 빈 화면·오류를 볼 수 있었다(2026-07-28 수정). */}
            <Route path="/franchise" element={<EB><RequireRole feature="estimate"><FranchiseHubPage /></RequireRole></EB>} />
            <Route path="/brands" element={<EB><RequireRole feature="brands"><BrandsPage /></RequireRole></EB>} />
            <Route path="/trash" element={<EB><RequireRole feature="estimate"><TrashPage /></RequireRole></EB>} />
            {/* 직원 관리 — 접근 config(기본 사장), 설정 안에서 진입 */}
            <Route path="/staff" element={<EB><RequireRole feature="staffadmin"><StaffAdmin /></RequireRole></EB>} />
            {/* SMBM(머니앱) — 앱 내 임베드(정산 하단). 접근 config(기본 임원+). 새 탭은 사이드바 ↗.
                lite=사장 보조 사업자(크레용 스튜디오) 공간 — 탭으로 분리(사장 요청 2026-07-24). */}
            <Route path="/smbm" element={<EB><RequireRole feature="smbm"><SmbmFrame tenant="main" /></RequireRole></EB>} />
            <Route path="/smbm-lite" element={<EB><RequireRole feature="smbm_lite"><SmbmFrame tenant="b2" /></RequireRole></EB>} />
            {/* AS·하자보수 — 고객 포털 접수 관리(manager+, 2026-07-25) */}
            <Route path="/as-admin" element={<EB><RequireRole feature="as_ops"><AsAdminPage /></RequireRole></EB>} />
            {/* 영업 파이프라인 — 문의→계약 보드(대표 전용, 2026-07-25) */}
            <Route path="/pipeline" element={<EB><RequireRole feature="pipeline"><PipelinePage /></RequireRole></EB>} />
            {/* 기타문서 — 현장팀 개방(사장 지시 2026-07-28). 견적 계열과 별도 키. */}
            <Route path="/docs" element={<EB><RequireRole feature="docs"><DocsPage /></RequireRole></EB>} />
            <Route path="/docs/new/:type" element={<EB><RequireRole feature="docs"><DocEditor /></RequireRole></EB>} />
            <Route path="/docs/:id" element={<EB><RequireRole feature="docs"><DocEditor /></RequireRole></EB>} />
            <Route path="/clipboard" element={<EB><RequireRole feature="estimate"><ClipboardPage /></RequireRole></EB>} />
          </Route>

          {/* ── 안전장치: 미정의 경로는 시작화면으로 ── */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* tart(FloatingAssistant) 숨김 — QCC 흡수 예정. 마운트만 제거(가역적). */}
        {/* <ErrorBoundary>
          <FloatingAssistant />
        </ErrorBoundary> */}
      </BrowserRouter>
    </ErrorBoundary>
  );

  // firebase 모드: 로그인·개별 계정 승인 게이트로 감싼다(공개 공유 페이지는 우회).
  if (!isPublicShare && AUTH_ENABLED) return <AuthGate>{appTree}</AuthGate>;
  return appTree;
}
